/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import isoFetch from 'isomorphic-fetch'
import qs from 'qs'
import moment from 'moment'

import { ApiError, ApiErrorMeta } from '../util/errors/ApiError'
import { API_URL } from '../config/env'
import { BaseError } from '../util/errors/BaseError'
import { ErrorGroup } from '../util/errors/ErrorGroup'
import { safeParseInt } from '../util/string'

type ApiErrorType = {
    code?: number
    message?: string
    details?: Record<string, any>
    _code?: number
    _message?: string
    _details?: Record<string, any>
}

const constructParams = (params: Record<string, unknown>): string | null => {
    const output: Record<string, unknown> = {}
    Object.entries(params).forEach(([key, value]) => {
        if (!value) return
        output[key] = value
    })
    if (Object.keys(output).length > 0) {
        return JSON.stringify(output)
    }
    return null
}

export type ApiMetaType = { total: number; page: number; size: number; count: number }

const buildIncludedEntities = (entities: Array<string> | string): string =>
    entities.length ? `&include=${Array.isArray(entities) ? entities.join(',') : entities}` : ''

const buildParameters = (params: Record<string, any>): string =>
    qs.stringify(
        {
            ...params,
        },
        {
            skipNulls: true,
            encode: false,
            arrayFormat: 'comma',
        }
    )

export const apiRequest = async <
    R = unknown,
    B extends Record<string, any> | unknown = unknown,
    // eslint-disable-next-line @typescript-eslint/ban-types
    M extends Record<string, any> = {}
>(params: {
    path: string
    method: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH'
    token?: string
    include?: Array<string> | string
    parameters?: Record<string, any>
    offset?: number
    limit?: number
    data?: B
    bodyFilter?: any
    disableCache?: boolean
    includeVehicles?: boolean
    meta?: M
}): Promise<
    | {
          data: R
          meta: { total: number; page: number; size: number } & M
      }
    | any
> => {
    const {
        path,
        method,
        token,
        offset,
        limit,
        include,
        bodyFilter,
        parameters: passedParameters = {},
        data,
        meta = {},
    } = params
    const headers = new Headers()

    headers.append('Content-Type', 'application/json')

    const page = constructParams({ offset, limit })
    let parameters = buildParameters({ page, ...passedParameters })

    if (include) {
        parameters += buildIncludedEntities(include)
    }
    if (token) {
        headers.append('Authorization', `JWT ${token}`)
    }

    let url = `${API_URL}${path}`
    if (parameters.length) url += `?${parameters}`

    let body: string | undefined
    if (data)
        body = JSON.stringify({
            meta,
            data,
        })
    if (bodyFilter) {
        const draftBody = body ? JSON.parse(body) : {}
        draftBody.filter = bodyFilter
        body = JSON.stringify(draftBody)
    }
    const response = await isoFetch(url, {
        method,
        headers,
        body,
    })

    let json

    if (response.status < 400) {
        try {
            json = await response.json()

            if (json.data || json.meta) {
                const { data: resultData, meta: resultMeta = {} } = json || {}

                const total: number = safeParseInt(resultMeta?.total) || 0
                const size: number = safeParseInt(resultMeta?.request?.query?.page?.size) || 0
                const pageNumber: number =
                    safeParseInt(resultMeta?.request?.query?.page?.number) || 0

                return {
                    data: resultData,
                    meta: {
                        ...(resultMeta as M),
                        total,
                        page: pageNumber,
                        size,
                    },
                }
            }
        } catch (error) {
            console.error('[apiRequests]:', error)
        }

        throw new ApiError({
            type: 'InternalServerError',
            cause: new Error('Response type missing "data"'),
        })
    }

    const parseError = (apiError: ApiErrorType): ApiError | BaseError => {
        const error = new Error(apiError.message || apiError._message)
        const nextMeta: ApiErrorMeta = {
            message: apiError.message || apiError._message,
            code: apiError.code || apiError._code,
            details: apiError.details || apiError._details,
        }
        switch (apiError.code || apiError._code) {
            case 400:
                return new ApiError({
                    type: 'BadRequest',
                    cause: error,
                    meta: nextMeta,
                })
            case 401:
                return new ApiError({
                    type: 'Unauthorized',
                    cause: error,
                    meta: nextMeta,
                })
            case 403:
                return new ApiError({
                    type: 'Forbidden',
                    cause: error,
                    meta: nextMeta,
                })
            case 404:
                return new ApiError({
                    type: 'NotFound',
                    cause: error,
                    meta: nextMeta,
                })
            case 500:
                throw new ApiError({
                    type: 'InternalServerError',
                    cause: error,
                    meta
                })
            case 501:
            case 502:
            case 503:
                return new ApiError({
                    type: 'ServiceUnavailable',
                    cause: error,
                    meta,
                })
            case 504:
                return new ApiError({
                    type: 'InternalServerError',
                    cause: error,
                    meta,
                })
            default:
                return new BaseError({ cause: error, message: apiError.message })
        }
    }

    try {
        try {
            json = await response.json()
        } catch {
            // noop
        }

        const { errors }: { errors: ApiErrorType[] } = json
        const parsedErrors = errors.map(parseError)

        if (parsedErrors.length === 1) {
            throw parsedErrors[0]
        }

        throw new ErrorGroup(parsedErrors)
    } catch (error: any) {
        console.error('[apiRequests]:', JSON.stringify(error))

        if (error.code === 401) {
            return new ApiError({
                type: 'Unauthorized',
                cause: error,
            })
        }

        throw new BaseError({
            cause: new Error('Error parsing JSON data from the server:', error)
        })
    }
}

const buildCSVFilename = (date, inventoryOnly, exportDate, timezone) => {
    if (inventoryOnly && exportDate) {
        const dateTime = moment(exportDate).format('YYYY-MM-DD-HH-mm-ss')
        const timezoneAbbr = moment.tz(exportDate, timezone).zoneAbbr()
        return `navtrac.yardreport.${dateTime}-${timezoneAbbr}.csv`
    }
    return `navtrac.report.${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}.csv`
}

export const apiStreamReader = async (params: {
    path: string
    token?: string
    include?: Array<string> | string
    parameters?: Record<string, any>
    data?: Record<string, any>
    includeVehicles?: boolean
    inventoryOnly?: boolean
    timezone?: string
}): Promise<{
    reader: ReadableStreamDefaultReader<Uint8Array>
    meta: { contentLength: number; fileName: string }
}> => {
    const { data, include, token, parameters: passedParameters, path, inventoryOnly, timezone } = params
    const headers = new Headers()

    headers.append('Content-Type', 'application/json')

    let parameters
    if (passedParameters) {
        parameters = buildParameters({ ...passedParameters })
    }
    let url = `${API_URL}${path}`
    if (include) {
        parameters += buildIncludedEntities(include)
    }
    if (parameters && parameters.length) url += `?${parameters}`
    if (token) {
        headers.append('Authorization', `JWT ${token}`)
    }
    const body = data ? JSON.stringify({ data }) : undefined
    const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
    })

    const contentLength = +(response.headers.get('Content-Length') || '')
    const contentDisposition = response.headers.get('Content-Disposition') || ''
    const today = new Date()
    let [
        ,
        fileName = buildCSVFilename(today, inventoryOnly, data?.exportDate, timezone),
    ] = contentDisposition.split('=')
    fileName = fileName.replace('"', '')
    if (response.status >= 400) {
        throw new ApiError({ type: 'InternalServerError' })
    }
    const reader = response.body?.getReader()

    if (!reader) throw new ApiError({ type: 'InternalServerError' })

    return {
        reader,
        meta: {
            contentLength,
            fileName,
        },
    }
}
